import LuxonAdapter from "@date-io/luxon";
import { authServiceImpl, ExpiredSessionModal } from "@lvt/expired-session-modal";
import globals from "@lvt/globals";
import { theme } from "@lvt/material";
import {
    SecurityAlertsNotificationContextWrapper,
    SecurityAlertsNotificationService,
    SocketIOSecurityAlertsNotificationService,
} from "@lvt/security-alerts-notification-service-client";
import {
    SocketIOViewNotificationService,
    ViewNotificationContextWrapper,
    ViewNotificationService,
} from "@lvt/view-notification-service-client";
import { CssBaseline } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { ThemeProvider } from "@material-ui/styles";
import { SnackbarProvider } from "notistack";
import { useEffect, useRef, useState } from "react";
import { Provider } from "react-redux";
import { Route, Switch } from "react-router-dom";

import client from "@/api/client";
import { fetchClients } from "@/api/clients";
import { archiveMetadataService, presetImagesService, videoRetrievalService } from "@/api/services/implementations";
import { alertService } from "@/api/services/implementations/alert";
import { fetchUserIdentity, fetchUserInfo, UserInfo } from "@/api/user";
import { FeatureHubProvider } from "@/common/components/Featurehub";
import EmptyLayout from "@/common/containers/EmptyLayout";
import SideNavLayout from "@/common/containers/SideNavLayout";
import { ArchiveMetadataContextWrapper } from "@/common/contexts/ArchiveMetadataContext";
import { PresetImagesContextWrapper } from "@/common/contexts/PresetImagesContext";
import { VideoRetrievalContextWrapper } from "@/common/contexts/VideoRetrievalContext";
import { i18nInit } from "@/i18n";
import { createStore } from "@/store";
import { addAlert, Severity } from "@/store/alerts/slice";
import { setUserIdentity, setUserSession } from "@/store/user/slice";
import { OnFeatureFlag } from "@/utilities/OnFeatureFlag";
import { useFullStory } from "@/utilities/hooks/fullstory";
import LvtUrls from "@/utilities/types/lvtUrls";
import { MapLandingPage } from "@/views";
import AlertsPage from "@/views/alerts/AlertsPage/AlertsPage";
import { LiveUnitPage } from "@/views/liveUnit/LiveUnitPage";
import LiveUnitMosaicPage from "@/views/liveUnitMosaic/LiveUnitMosaicPage";
import MosaicPage from "@/views/mosaic/MosaicPage";

import ProductEntitlementsProvider from "../contexts/productEntitlementsContext";


interface AppProps {
    d3Url: string;
    portalUrl: string;
    selfServiceUrl: string;
    tokenUrl: string;
    vmsUrl: string;
    nextAppUrl: string;
    realtimeEventsUrl: string;
    walkMeScript: string;
}

export const store = createStore();

store.dispatch(fetchClients());

// start loading i18n before app renders.
const loadI18n = i18nInit();

const usePageStyles = makeStyles(() =>
    createStyles({
        snackBarError: {
            marginRight: "5em",
            backgroundColor: "#FDECEA",
            padding: "0 13px 0 0",
        },
        snackBarWarning: {
            marginRight: "5em",
            backgroundColor: "#FFF4E5",
            padding: "0 13px 0 0",
        },
        snackBarSuccess: {
            marginRight: "5em",
            backgroundColor: "#EDF7ED",
            padding: "0 13px 0 0",
        },
    }),
);

export const App: React.FC<AppProps> = ({
    tokenUrl,
    portalUrl,
    d3Url,
    selfServiceUrl,
    vmsUrl,
    nextAppUrl,
    realtimeEventsUrl,
    walkMeScript,
}) => {
    const FullStory = useFullStory();
    const pageClasses = usePageStyles();
    const [isAuthenticated, setIsAuthenticated] = useState(true);
    const authService = authServiceImpl(client);
    const [i18nLoaded, seti18nLoaded] = useState(false);

    const securityAlertsServiceRef = useRef<SecurityAlertsNotificationService | null>(null);
    const getSecurityAlertsService = (): SecurityAlertsNotificationService => {
        if (!securityAlertsServiceRef.current) {
            // React suggests this pattern: https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents
            securityAlertsServiceRef.current = new SocketIOSecurityAlertsNotificationService(
                `${realtimeEventsUrl}/security_alerts`,
            );
        }

        return securityAlertsServiceRef.current;
    };

    const viewServiceRef = useRef<ViewNotificationService | null>(null);
    const getViewService = (): ViewNotificationService => {
        if (!viewServiceRef.current) {
            // React suggests this pattern: https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents
            viewServiceRef.current = new SocketIOViewNotificationService(`${realtimeEventsUrl}/view`);
        }

        return viewServiceRef.current;
    };

    const onAuthCheck = async () => {
        const isAuthed = await authService.authCheck();

        setIsAuthenticated(isAuthed);
    };

    useEffect(() => {
        fetchUserIdentity(tokenUrl)
            .then((userIdentity) => {
                loadWalkMe(userIdentity.userId);
                store.dispatch(setUserIdentity(userIdentity));
                store.dispatch(setUserSession());
                getSecurityAlertsService().setAuthToken(userIdentity.userToken);
                getViewService().setAuthToken(userIdentity.userToken);
            })
            .catch(() => {
                window.location.href = "/auth/redirect";
            });

        loadI18n.then(() => seti18nLoaded(true)).catch((e) => console.error("failed to load i18n", e));

        store
            .dispatch(fetchUserInfo())
            .unwrap()
            .then((userInfo: UserInfo) => {
                FullStory.identify(userInfo.id.toString(), {
                    displayName: userInfo.name,
                    email: userInfo.username,
                });
            })
            .catch((error) => {
                console.log(error);
                store.dispatch(
                    addAlert({
                        severity: Severity.Error,
                        message: "Failed to retrieve user info",
                    }),
                );
            });
    }, []);

    const lvtUrls: LvtUrls = {
        portal: portalUrl,
        d3: d3Url,
        vms: vmsUrl,
        selfService: selfServiceUrl,
        nextApp: nextAppUrl,
    };

    const { publicServerApiKey } = globals.featureHub;

    return (
        <ViewNotificationContextWrapper service={getViewService()}>
            <VideoRetrievalContextWrapper service={videoRetrievalService}>
                <ArchiveMetadataContextWrapper service={archiveMetadataService}>
                    <PresetImagesContextWrapper service={presetImagesService}>
                        <Provider store={store}>
                            <ProductEntitlementsProvider nextAppUrl={globals.nextApp.url}>
                                <FeatureHubProvider
                                    edgeUrl={globals.nextApp.url}
                                    apiKey={publicServerApiKey}
                                >
                                    <ThemeProvider theme={theme}>
                                        <MuiPickersUtilsProvider utils={LuxonAdapter}>
                                            <CssBaseline />
                                            <SnackbarProvider
                                                hideIconVariant
                                                classes={{
                                                    variantError: pageClasses.snackBarError,
                                                    variantWarning: pageClasses.snackBarWarning,
                                                    variantSuccess: pageClasses.snackBarSuccess,
                                                }}
                                                maxSnack={4}
                                            >
                                                {i18nLoaded && (
                                                    <>
                                                        <ExpiredSessionModal
                                                            isAuthenticated={isAuthenticated}
                                                            onAuthCheck={onAuthCheck}
                                                        />
                                                        <Switch>
                                                            <Route path="/mosaic/:mosaicId">
                                                                <EmptyLayout>
                                                                    <MosaicPage />
                                                                </EmptyLayout>
                                                            </Route>
                                                            <Route path="/alerts">
                                                                <SideNavLayout urls={lvtUrls}>
                                                                    <OnFeatureFlag flag="security_alerts" redirect="/">
                                                                        <SecurityAlertsNotificationContextWrapper
                                                                            service={getSecurityAlertsService()}
                                                                        >
                                                                            <AlertsPage alertService={alertService} />
                                                                        </SecurityAlertsNotificationContextWrapper>
                                                                    </OnFeatureFlag>
                                                                </SideNavLayout>
                                                            </Route>
                                                            <Route path="/live-unit/:liveUnitId/view">
                                                                <EmptyLayout>
                                                                    <OnFeatureFlag flag="security_alerts" redirect="/">
                                                                        <SecurityAlertsNotificationContextWrapper
                                                                            service={getSecurityAlertsService()}
                                                                        >
                                                                            <LiveUnitMosaicPage
                                                                                alertService={alertService}
                                                                            />
                                                                        </SecurityAlertsNotificationContextWrapper>
                                                                    </OnFeatureFlag>
                                                                </EmptyLayout>
                                                            </Route>
                                                            <Route
                                                                path={[
                                                                    "/camera/:cameraId/stream",
                                                                    "/live-unit/:liveUnitId/history",
                                                                    "/live-unit/:liveUnitId/requested-video",
                                                                    "/archive-cabinet/:cabinetId/history",
                                                                ]}
                                                            >
                                                                <SideNavLayout urls={lvtUrls}>
                                                                    <LiveUnitPage />
                                                                </SideNavLayout>
                                                            </Route>
                                                            <Route path="/">
                                                                <SideNavLayout urls={lvtUrls}>
                                                                    <MapLandingPage />
                                                                </SideNavLayout>
                                                            </Route>
                                                        </Switch>
                                                    </>
                                                )}
                                            </SnackbarProvider>
                                        </MuiPickersUtilsProvider>
                                    </ThemeProvider>
                                </FeatureHubProvider>
                            </ProductEntitlementsProvider>
                        </Provider>
                    </PresetImagesContextWrapper>
                </ArchiveMetadataContextWrapper>
            </VideoRetrievalContextWrapper>
        </ViewNotificationContextWrapper>
    );

    function loadWalkMe(userId: number) {
        try {
            /* @ts-ignore: Provide global UUID for WalkME to scrape */
            window.USERID = userId;

            // Add WalkME script tag
            const walkme = document.createElement("script");

            walkme.type = "text/javascript";
            walkme.async = true;
            walkme.src = walkMeScript;

            const s = document.getElementsByTagName("script")[0];

            s?.parentNode?.insertBefore(walkme, s);
            /* @ts-ignore */
            window._walkmeConfig = { smartLoad: true };
        } catch (e) {
            console.log("error loading WalkME", e);
        }
    }
};
